import React, { useMemo } from "react";
import { MATERIALS, ROOM_TEMPLATES, DEFAULT_H } from "../../constants";
import { Geo } from "../../utils/geo";
import { LINE, DIM_STYLE } from "../../engine/drawingStandards";
import {
  HatchDefs, WallHatch, DimLine, NorthArrow, ScaleBar, TitleBlock,
  DoorSymbol, WindowSymbol, mmToPx, worldToPx,
} from "./DrawingUtils";

const mm = (v) => `${(v/1000).toFixed(3)}`;  // format mm as metres string

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getWallPoly(nA, nB, thick, scale) {
  const pA = { x: worldToPx(nA.x, scale), y: worldToPx(nA.y, scale) };
  const pB = { x: worldToPx(nB.x, scale), y: worldToPx(nB.y, scale) };
  const dx = pB.x - pA.x, dy = pB.y - pA.y, L = Math.sqrt(dx*dx + dy*dy);
  if (L < 0.5) return null;
  const T  = worldToPx(thick, scale) / 2;
  const nx = -dy/L, ny = dx/L;
  return {
    pA, pB, L, nx, ny, T,
    poly: [
      [pA.x + nx*T, pA.y + ny*T],
      [pB.x + nx*T, pB.y + ny*T],
      [pB.x - nx*T, pB.y - ny*T],
      [pA.x - nx*T, pA.y - ny*T],
    ],
    polyStr: [
      `${pA.x + nx*T},${pA.y + ny*T}`,
      `${pB.x + nx*T},${pB.y + ny*T}`,
      `${pB.x - nx*T},${pB.y - ny*T}`,
      `${pA.x - nx*T},${pA.y - ny*T}`,
    ].join(" "),
  };
}

// ─── Single wall with hatch, cut outline, and opening cutouts ─────────────────
function DrawnWall({ edge, st, scale, isSelected }) {
  const ab = st.halfEdges[edge.halfEdgeA], ba = st.halfEdges[edge.halfEdgeB];
  if (!ab || !ba) return null;
  const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
  if (!nA || !nB) return null;
  const thick = edge.thickness || 230;
  const geo   = getWallPoly(nA, nB, thick, scale);
  if (!geo) return null;

  const matId = edge.materialId || "BRICK";
  const mat   = MATERIALS[matId];
  const ops   = Object.values(st.openings || {}).filter(o => o.edgeId === edge.id);
  const sw    = mmToPx(LINE.OUTLINE.weight);
  const swThin= mmToPx(LINE.VISIBLE.weight);

  // Build clipping path that cuts out openings from the wall polygon
  const clipId = `wall-clip-${edge.id}`;
  const { pA, pB, L, nx, ny, T } = geo;

  const openingRects = ops.map(op => {
    const cx  = pA.x + (pB.x - pA.x) * op.t;
    const cy  = pA.y + (pB.y - pA.y) * op.t;
    const hw  = worldToPx(op.width, scale) / 2;
    const ux  = (pB.x - pA.x) / L, uy = (pB.y - pA.y) / L;
    const x1  = cx - ux*hw - nx*(T+2), y1 = cy - uy*hw - ny*(T+2);
    const ww  = worldToPx(op.width, scale) + 4, wh = T*2 + 4;
    const ang = Math.atan2(pB.y-pA.y, pB.x-pA.x) * 180/Math.PI;
    return { x: cx - ux*hw, y: cy - uy*hw - ny*T, w: ww, h: T*2, ang, cx, cy, hw, op };
  });

  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <polygon points={geo.polyStr}/>
        </clipPath>
      </defs>

      {/* Hatch fill clipped to wall polygon */}
      <g clipPath={`url(#${clipId})`}>
        <WallHatch
          x={Math.min(pA.x, pB.x) - T - 2}
          y={Math.min(pA.y, pB.y) - T - 2}
          w={L + T*2 + 4}
          h={T*2 + 4}
          matId={matId}
          isSection={true}
        />
      </g>

      {/* Opening white-out */}
      {openingRects.map(({ cx, cy, hw, op }) => {
        const ux = (pB.x-pA.x)/L, uy = (pB.y-pA.y)/L;
        const ox1 = cx - ux*hw, oy1 = cy - uy*hw;
        return (
          <polygon key={op.id}
            points={[
              `${ox1 + nx*(T+1)},${oy1 + ny*(T+1)}`,
              `${ox1 + ux*worldToPx(op.width,scale) + nx*(T+1)},${oy1 + uy*worldToPx(op.width,scale) + ny*(T+1)}`,
              `${ox1 + ux*worldToPx(op.width,scale) - nx*(T+1)},${oy1 + uy*worldToPx(op.width,scale) - ny*(T+1)}`,
              `${ox1 - nx*(T+1)},${oy1 - ny*(T+1)}`,
            ].join(" ")}
            fill="white" stroke="none"
          />
        );
      })}

      {/* Wall outline — heavy lines */}
      <polygon points={geo.polyStr} fill="none"
        stroke={isSelected ? "#e8a838" : "#000"}
        strokeWidth={isSelected ? sw*2 : sw}/>

      {/* Window lines */}
      {ops.filter(op => op.sillHeight > 0).map(op => {
        const cx  = pA.x + (pB.x - pA.x) * op.t;
        const cy  = pA.y + (pB.y - pA.y) * op.t;
        const hw  = worldToPx(op.width, scale) / 2;
        const ux  = (pB.x-pA.x)/L, uy = (pB.y-pA.y)/L;
        return (
          <g key={op.id}>
            {/* Three parallel lines across opening */}
            {[0.2, 0.5, 0.8].map((f, i) => (
              <line key={i}
                x1={cx - ux*hw + nx*T*(2*f-1)} y1={cy - uy*hw + ny*T*(2*f-1)}
                x2={cx + ux*hw + nx*T*(2*f-1)} y2={cy + uy*hw + ny*T*(2*f-1)}
                stroke="#000" strokeWidth={swThin}/>
            ))}
          </g>
        );
      })}

      {/* Door arcs */}
      {ops.filter(op => !op.sillHeight || op.sillHeight === 0).map(op => {
        const cx  = pA.x + (pB.x - pA.x) * op.t;
        const cy  = pA.y + (pB.y - pA.y) * op.t;
        const hw  = worldToPx(op.width, scale) / 2;
        const ux  = (pB.x-pA.x)/L, uy = (pB.y-pA.y)/L;
        const door1x = cx - ux*hw, door1y = cy - uy*hw;
        const door2x = cx - ux*hw + nx*T*2, door2y = cy - uy*hw + ny*T*2;
        const arcR   = worldToPx(op.width, scale);
        const endX   = door1x + ux*worldToPx(op.width,scale);
        const endY   = door1y + uy*worldToPx(op.width,scale);
        return (
          <g key={op.id}>
            {/* Door leaf line */}
            <line x1={door1x} y1={door1y} x2={endX} y2={endY} stroke="#000" strokeWidth={swThin}/>
            {/* Door swing arc */}
            <path d={`M ${door1x} ${door1y} A ${arcR} ${arcR} 0 0 1 ${door2x} ${door2y}`}
              fill="none" stroke="#000" strokeWidth={mmToPx(0.18)}
              strokeDasharray={`${mmToPx(3)} ${mmToPx(2)}`}/>
          </g>
        );
      })}
    </g>
  );
}

// ─── Room label (construction doc style) ─────────────────────────────────────
function RoomLabel({ face, rooms, st, scale }) {
  if (!face.centroid) return null;
  const cx = worldToPx(face.centroid.x, scale);
  const cy = worldToPx(face.centroid.y, scale);
  const rd = (rooms || {})[face.id] || { type: "ROOM", name: "" };
  const tmpl = ROOM_TEMPLATES[rd.type] || ROOM_TEMPLATES.ROOM;
  const areaM2 = (face.area / 1e6).toFixed(2);
  const ts = mmToPx(2.5);

  return (
    <g style={{ pointerEvents: "none" }}>
      <text x={cx} y={cy - mmToPx(2)} textAnchor="middle" fontSize={ts}
        fontWeight="700" fontFamily={DIM_STYLE.font} fill="#000">
        {rd.name || tmpl.label}
      </text>
      <text x={cx} y={cy + mmToPx(2)} textAnchor="middle" fontSize={mmToPx(2)}
        fontFamily={DIM_STYLE.font} fill="#555">
        {areaM2} m²
      </text>
    </g>
  );
}

// ─── Dimension chain for all walls along a given axis ─────────────────────────
function DimChain({ st, scale, axis, offset, sheetOffsetX, sheetOffsetY }) {
  // Collect all unique X (or Y) coordinates of nodes
  const coords = new Set();
  Object.values(st.nodes).forEach(n => {
    coords.add(axis === "h" ? n.x : n.y);
  });
  const sorted = [...coords].sort((a, b) => a - b);
  if (sorted.length < 2) return null;

  const off   = mmToPx(offset);
  const sw    = mmToPx(LINE.DIMENSION.weight);
  const ts    = mmToPx(2.5);
  const extG  = mmToPx(2);
  const extL  = mmToPx(3);

  return (
    <g style={{ pointerEvents: "none" }}>
      {sorted.map((v, i) => {
        if (i === sorted.length - 1) return null;
        const next = sorted[i + 1];
        const dist = next - v;
        if (dist < 50) return null; // skip tiny gaps
        const mid = (v + next) / 2;
        const dimV = worldToPx(v, scale)    + (axis === "h" ? sheetOffsetX : sheetOffsetY);
        const dimN = worldToPx(next, scale) + (axis === "h" ? sheetOffsetX : sheetOffsetY);
        const dimM = (dimV + dimN) / 2;

        if (axis === "h") {
          const fixedY = sheetOffsetY - off;
          return (
            <g key={`dim-h-${i}`}>
              <line x1={dimV} y1={sheetOffsetY - extG} x2={dimV} y2={fixedY - extL} stroke="#000" strokeWidth={sw}/>
              <line x1={dimN} y1={sheetOffsetY - extG} x2={dimN} y2={fixedY - extL} stroke="#000" strokeWidth={sw}/>
              <line x1={dimV} y1={fixedY} x2={dimN} y2={fixedY} stroke="#000" strokeWidth={sw}
                markerStart="url(#dim-tick)" markerEnd="url(#dim-tick)"/>
              <text x={dimM} y={fixedY - mmToPx(1.5)} textAnchor="middle"
                fontSize={ts} fontFamily={DIM_STYLE.font} fill="#000">
                {dist >= 1000 ? `${(dist/1000).toFixed(2)}m` : `${dist}mm`}
              </text>
            </g>
          );
        } else {
          const fixedX = sheetOffsetX - off;
          return (
            <g key={`dim-v-${i}`}>
              <line x1={sheetOffsetX - extG} y1={dimV} x2={fixedX - extL} y2={dimV} stroke="#000" strokeWidth={sw}/>
              <line x1={sheetOffsetX - extG} y1={dimN} x2={fixedX - extL} y2={dimN} stroke="#000" strokeWidth={sw}/>
              <line x1={fixedX} y1={dimV} x2={fixedX} y2={dimN} stroke="#000" strokeWidth={sw}
                markerStart="url(#dim-tick)" markerEnd="url(#dim-tick)"/>
              <text x={fixedX - mmToPx(2)} y={dimM + mmToPx(1)} textAnchor="middle"
                fontSize={ts} fontFamily={DIM_STYLE.font} fill="#000"
                transform={`rotate(-90,${fixedX - mmToPx(2)},${dimM})`}>
                {dist >= 1000 ? `${(dist/1000).toFixed(2)}m` : `${dist}mm`}
              </text>
            </g>
          );
        }
      })}
    </g>
  );
}

// ─── Main floor plan drawing ───────────────────────────────────────────────────
export default function FloorPlanDrawing({
  st, scale = 100, selEdgeId,
  sheetSize = { w: 420, h: 297 },  // A3 landscape in mm
  northRotation = 0,
  drawingNo = "PC-FP-01",
}) {
  const project = st.project || {};
  const margin  = mmToPx(15);
  const titleH  = mmToPx(30);
  const dimSpace= mmToPx(25);  // space for dimension chains

  // Sheet dimensions in px
  const sheetW = mmToPx(sheetSize.w);
  const sheetH = mmToPx(sheetSize.h);

  // Usable drawing area (inside margin, above title block, with dim space)
  const drawX = margin + dimSpace;
  const drawY = margin + dimSpace;
  const drawW = sheetW - margin - dimSpace - margin;
  const drawH = sheetH - titleH - margin*2 - dimSpace;

  // Compute bounding box of plan
  const nodes = Object.values(st.nodes || {});
  if (!nodes.length) return (
    <svg width={sheetW} height={sheetH} style={{ background: "#fff" }}>
      <HatchDefs scale={scale}/>
      <rect width={sheetW} height={sheetH} fill="#fff" stroke="#000" strokeWidth={mmToPx(0.5)}/>
      <text x={sheetW/2} y={sheetH/2} textAnchor="middle" fontSize={mmToPx(4)} fontFamily={DIM_STYLE.font} fill="#aaa">
        No geometry — draw walls first
      </text>
    </svg>
  );

  const xs = nodes.map(n => n.x), ys = nodes.map(n => n.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const planWpx = worldToPx(maxX - minX, scale);
  const planHpx = worldToPx(maxY - minY, scale);

  // Centre the plan in the drawing area
  const offX = drawX + (drawW - planWpx) / 2 - worldToPx(minX, scale);
  const offY = drawY + (drawH - planHpx) / 2 - worldToPx(minY, scale);

  return (
    <svg
      width={sheetW} height={sheetH}
      viewBox={`0 0 ${sheetW} ${sheetH}`}
      style={{ background: "#fff", display: "block" }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <HatchDefs scale={scale}/>

      {/* Sheet border — double line */}
      <rect x={mmToPx(5)} y={mmToPx(5)} width={sheetW-mmToPx(10)} height={sheetH-mmToPx(10)}
        fill="none" stroke="#000" strokeWidth={mmToPx(0.5)}/>
      <rect x={mmToPx(10)} y={mmToPx(10)} width={sheetW-mmToPx(20)} height={sheetH-mmToPx(20)}
        fill="none" stroke="#000" strokeWidth={mmToPx(0.18)}/>

      {/* Plan drawings — offset to centred position */}
      <g transform={`translate(${offX}, ${offY})`}>
        {/* Room fills first */}
        {Object.values(st.faces || {}).map(face => {
          const pts = (face.loop || []).map(hid => {
            const he = st.halfEdges[hid]; if (!he) return null;
            const n  = st.nodes[he.originId]; if (!n) return null;
            return `${worldToPx(n.x, scale)},${worldToPx(n.y, scale)}`;
          }).filter(Boolean);
          if (pts.length < 3) return null;
          return <polygon key={face.id} points={pts.join(" ")} fill="#fafafa" stroke="none"/>;
        })}

        {/* Walls */}
        {Object.values(st.edges || {}).map(e => (
          <DrawnWall key={e.id} edge={e} st={st} scale={scale} isSelected={e.id === selEdgeId}/>
        ))}

        {/* Room labels */}
        {Object.values(st.faces || {}).map(face => (
          <RoomLabel key={face.id} face={face} rooms={st.rooms} st={st} scale={scale}/>
        ))}
      </g>

      {/* Dimension chains — planTopPx and planLeftPx are the sheet-pixel positions of the plan bounding box */}
      <DimChain st={st} scale={scale} axis="h" offset={12}
        sheetOffsetX={offX}
        sheetOffsetY={offY + worldToPx(minY, scale)}
      />
      <DimChain st={st} scale={scale} axis="v" offset={12}
        sheetOffsetX={offX + worldToPx(minX, scale)}
        sheetOffsetY={offY}
      />

      {/* Overall external dimensions */}
      <DimLine
        x1={offX + worldToPx(minX, scale)}
        y1={offY + worldToPx(minY, scale)}
        x2={offX + worldToPx(maxX, scale)}
        y2={offY + worldToPx(minY, scale)}
        offset={22}
        text={`${mm(maxX-minX)}m`}
        orientation="h"
      />
      <DimLine
        x1={offX + worldToPx(minX, scale)}
        y1={offY + worldToPx(minY, scale)}
        x2={offX + worldToPx(minX, scale)}
        y2={offY + worldToPx(maxY, scale)}
        offset={22}
        text={`${mm(maxY-minY)}m`}
        orientation="v"
      />


      {/* Door / Window Schedule */}
      {(() => {
        const ops = Object.values(st.openings||{});
        if (!ops.length) return null;
        const doors   = ops.filter(o => !o.sillHeight || o.sillHeight===0);
        const windows = ops.filter(o => o.sillHeight > 0);
        const schedX  = mmToPx(sheetSize.w * 0.60);
        const schedY  = sheetH - titleH - mmToPx(70);
        const rowH    = mmToPx(6);
        const colW    = [mmToPx(12), mmToPx(18), mmToPx(18), mmToPx(22)];
        const ts2     = mmToPx(2.2);
        const sw2     = mmToPx(0.18);
        const headers = ["Ref","W (mm)","H (mm)","Sill (mm)"];
        let rows = [
          ...doors.map((o,i)  => [`D${i+1}`, o.width, o.height, "—"]),
          ...windows.map((o,i)=> [`W${i+1}`, o.width, o.height, o.sillHeight]),
        ];
        const tableW = colW.reduce((a,b)=>a+b,0);
        const totalH = rowH*(rows.length+2);
        return (
          <g style={{ pointerEvents:"none" }}>
            <text x={schedX} y={schedY-mmToPx(3)} fontSize={mmToPx(3)} fontWeight="700"
              fontFamily={DIM_STYLE.font} fill="#000">DOOR / WINDOW SCHEDULE</text>
            {/* Header */}
            <rect x={schedX} y={schedY} width={tableW} height={rowH} fill="#e8e8e8" stroke="#000" strokeWidth={sw2}/>
            {headers.map((h,i)=>(
              <text key={i} x={schedX+colW.slice(0,i).reduce((a,b)=>a+b,0)+mmToPx(1)} y={schedY+rowH-mmToPx(1.5)}
                fontSize={ts2} fontFamily={DIM_STYLE.font} fontWeight="700" fill="#000">{h}</text>
            ))}
            {/* Rows */}
            {rows.map((row,ri)=>(
              <g key={ri}>
                <rect x={schedX} y={schedY+rowH*(ri+1)} width={tableW} height={rowH}
                  fill={ri%2===0?"#fafafa":"#fff"} stroke="#000" strokeWidth={sw2}/>
                {row.map((cell,ci)=>(
                  <text key={ci} x={schedX+colW.slice(0,ci).reduce((a,b)=>a+b,0)+mmToPx(1)}
                    y={schedY+rowH*(ri+2)-mmToPx(1.5)}
                    fontSize={ts2} fontFamily={DIM_STYLE.font} fill="#000">{String(cell)}</text>
                ))}
              </g>
            ))}
          </g>
        );
      })()}
      {/* North arrow */}
      <NorthArrow
        cx={margin + mmToPx(8)}
        cy={sheetH - titleH - mmToPx(20)}
        r={8}
        rotation={northRotation}
      />

      {/* Scale bar */}
      <ScaleBar
        x={margin + mmToPx(5)}
        y={sheetH - titleH - mmToPx(10)}
        scale={scale}
        segments={4}
      />


      {/* Section cut markers */}
      {((st.drawing||{}).sectionCuts||[]).map(cut => {
        const x1 = offX + worldToPx(cut.x1, scale), y1 = offY + worldToPx(cut.y1, scale);
        const x2 = offX + worldToPx(cut.x2, scale), y2 = offY + worldToPx(cut.y2, scale);
        const len = Math.sqrt((x2-x1)**2+(y2-y1)**2) || 1;
        const nx  = -(y2-y1)/len, ny = (x2-x1)/len;
        const tL  = mmToPx(6);
        const sw  = mmToPx(LINE.SECTION_CUT.weight);
        const ts  = mmToPx(3);
        return (
          <g key={cut.id} style={{ pointerEvents:"none" }}>
            <line x1={x1} y1={y1} x2={x2} y2={y2}
              stroke="#000" strokeWidth={sw}
              strokeDasharray={`${mmToPx(8)} ${mmToPx(4)} ${mmToPx(2)} ${mmToPx(4)}`}/>
            {/* Direction ticks at each end */}
            {[[x1,y1],[x2,y2]].map(([ex,ey],i) => (
              <g key={i}>
                <line x1={ex} y1={ey} x2={ex+nx*tL} y2={ey+ny*tL} stroke="#000" strokeWidth={sw}/>
                <circle cx={ex+nx*tL*1.6} cy={ey+ny*tL*1.6} r={tL*0.8} fill="none" stroke="#000" strokeWidth={mmToPx(0.35)}/>
                <text x={ex+nx*tL*1.6} y={ey+ny*tL*1.6+mmToPx(1)} textAnchor="middle"
                  fontSize={ts} fontFamily={DIM_STYLE.font} fontWeight="700" fill="#000">
                  {cut.label||"A"}
                </text>
              </g>
            ))}
          </g>
        );
      })}
      {/* Title block */}
      <TitleBlock
        x={mmToPx(10)} y={sheetH - titleH + mmToPx(2)}
        w={sheetW - mmToPx(20)} h={titleH - mmToPx(4)}
        project={project}
        drawingTitle="FLOOR PLAN"
        drawingNo={drawingNo}
        scale={scale}
        date={project.date}
      />
    </svg>
  );
}
